import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { Weekday } from '@chancenwerk/enums/enums.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import { CourseLernfoerderung } from '@chancenwerk/shared/courses/CourseLernfoerderung.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { map, of, switchMap } from 'rxjs';

const courseSelector: BaseQLSelectionSet<Course> = { weekday: 1, startTime: 1, endTime: 1, startDate: 1 };

/**
 * @customElement kraefte-component-capacity-courses-popup
 */
@customElement('kraefte-component-capacity-courses-popup')
export class KraefteComponentCapacityCoursesPopup extends ChemistryLitElement {
  @property() kpfID = new RXController<Maybe<string>>(this, null);

  @state() private readonly _selectedCourses = new RXController(
    this,
    this.kpfID.observable.pipe(
      switchMap(id => (id ? Course.subscribeAllCoursesByKPFID(id)(courseSelector) : of([] as Course[]))),
      map(courses => courses.filter(c => c._class === CourseLernfoerderung._class)),
    ),
  );

  override render() {
    const mappedByDay: Record<string, Course[]> =
      this._selectedCourses.value?.reduce((acc, course) => {
        const day = course.weekday;
        if (!day) {
          return acc;
        }

        if (!acc[day]) {
          acc[day] = [];
        }

        acc[day].push(course);
        return acc;
      }, {}) ?? {};

    return html`<cr-flex space="md">
      <cr-text bold ${css({ marginBottom: this.spacing.md })}>Lernfördertage und -uhrzeiten:</cr-text>
      <cr-grid columns="100px 1fr" crossaxis-center space="md">
        ${Object.values(Weekday).map(day => {
          const courses = mappedByDay[day];
          if (!courses || courses.length === 0) {
            return nothing;
          }
          return html`<cr-text size="md">${day}:</cr-text>
            <cr-text
              >${[
                ...new Set(
                  courses.map(
                    course =>
                      `${course.startTime?.toFormat('HH:mm') ?? '???'}-${course.endTime?.toFormat('HH:mm') ?? '???'}`,
                  ),
                ),
              ].join(', ')}</cr-text
            >`;
        })}
      </cr-grid>
    </cr-flex>`;
  }
}
